import { toast } from 'sonner';
import {
  Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType,
} from 'docx';

export const DRAFT_TYPE_LABELS = {
  reply: 'Draft Reply',
  summary_for_management: 'Management Summary',
  legal_brief: 'Legal Brief',
  writ_petition: 'Writ Petition (Art. 226/227)',
  scn_reply_cgst_sgst: 'SCN Reply (CGST/SGST Act)',
  gst_appeal: 'GST Appeal',
  retraction_statement: 'Statement Retraction',
  adjournment_letter: 'Adjournment Letter',
};

const parseInlineFormatting = (text) => {
  const runs = [];
  const regex = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|([^*]+))/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match[2]) {
      runs.push(new TextRun({ text: match[2], bold: true, italics: true, size: 22 }));
    } else if (match[3]) {
      runs.push(new TextRun({ text: match[3], bold: true, size: 22 }));
    } else if (match[4]) {
      runs.push(new TextRun({ text: match[4], italics: true, size: 22 }));
    } else if (match[5]) {
      runs.push(new TextRun({ text: match[5], size: 22 }));
    }
  }
  return runs.length ? runs : [new TextRun({ text, size: 22 })];
};

const parseMarkdownToDocxParagraphs = (text) => {
  const paragraphs = [];
  const lines = text.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      paragraphs.push(new Paragraph({ spacing: { after: 100 } }));
      continue;
    }

    if (trimmed.startsWith('### ')) {
      paragraphs.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun({ text: trimmed.replace(/^###\s*/, ''), bold: true, size: 24 })],
        spacing: { before: 200, after: 80 },
      }));
    } else if (trimmed.startsWith('## ')) {
      paragraphs.push(new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: [new TextRun({ text: trimmed.replace(/^##\s*/, ''), bold: true, size: 28 })],
        spacing: { before: 240, after: 100 },
      }));
    } else if (trimmed.startsWith('# ')) {
      paragraphs.push(new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: [new TextRun({ text: trimmed.replace(/^#\s*/, ''), bold: true, size: 32 })],
        spacing: { before: 300, after: 120 },
      }));
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      const bulletText = trimmed.replace(/^[-*]\s*/, '');
      const runs = parseInlineFormatting(bulletText);
      paragraphs.push(new Paragraph({
        bullet: { level: 0 },
        children: runs,
        spacing: { after: 40 },
      }));
    } else if (/^\d+\.\s/.test(trimmed)) {
      const numText = trimmed.replace(/^\d+\.\s*/, '');
      const runs = parseInlineFormatting(numText);
      paragraphs.push(new Paragraph({
        numbering: { reference: 'numbered-list', level: 0 },
        children: runs,
        spacing: { after: 40 },
      }));
    } else if (trimmed.startsWith('---') || trimmed.startsWith('***')) {
      paragraphs.push(new Paragraph({
        children: [new TextRun({ text: '─'.repeat(50), color: '999999', size: 16 })],
        spacing: { before: 100, after: 100 },
      }));
    } else {
      const runs = parseInlineFormatting(trimmed);
      paragraphs.push(new Paragraph({
        children: runs,
        spacing: { after: 80 },
        alignment: AlignmentType.JUSTIFIED,
      }));
    }
  }
  return paragraphs;
};

// Manually trigger a file download via a hidden anchor. More reliable than
// file-saver in some production browser contexts (CSP, async user-gesture loss).
const triggerBlobDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.style.display = 'none';
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  window.document.body.appendChild(anchor);
  anchor.click();
  setTimeout(() => {
    try { window.document.body.removeChild(anchor); } catch (e) { /* noop */ }
    window.URL.revokeObjectURL(url);
  }, 200);
};

export const downloadAsWord = async (content, title, docType, filename) => {
  try {
    if (!content || !String(content).trim()) {
      toast.error('No content available to download');
      return;
    }
    const headerParagraphs = [
      new Paragraph({
        children: [new TextRun({ text: 'AKS TAX & LEGAL CONSULTANCY', bold: true, size: 28, color: '1E3A8A' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 40 },
      }),
      new Paragraph({
        children: [new TextRun({ text: 'Sole Proprietorship of Ashok Kumar Singh', size: 18, color: '666666', italics: true })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 40 },
      }),
      new Paragraph({
        children: [new TextRun({ text: 'GSTIN: 22ANOPS4530N1ZK', size: 16, color: '888888' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [new TextRun({ text: '─'.repeat(60), color: 'CCCCCC', size: 16 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 120 },
      }),
      new Paragraph({
        children: [new TextRun({ text: docType.toUpperCase(), bold: true, size: 26, color: '1E3A8A' })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 40 },
      }),
    ];

    if (title) {
      headerParagraphs.push(new Paragraph({
        children: [new TextRun({ text: `Document: ${title}`, size: 20, color: '444444', italics: true })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 40 },
      }));
    }

    headerParagraphs.push(
      new Paragraph({
        children: [new TextRun({
          text: `Generated: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}`,
          size: 18, color: '888888',
        })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 160 },
      }),
      new Paragraph({
        children: [new TextRun({ text: '─'.repeat(60), color: 'CCCCCC', size: 16 })],
        alignment: AlignmentType.CENTER,
        spacing: { after: 200 },
      }),
    );

    const contentParagraphs = parseMarkdownToDocxParagraphs(content);

    const doc = new Document({
      numbering: {
        config: [{
          reference: 'numbered-list',
          levels: [{
            level: 0,
            format: 'decimal',
            text: '%1.',
            alignment: AlignmentType.START,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } },
          }],
        }],
      },
      sections: [{
        properties: {
          page: { margin: { top: 1440, bottom: 1440, left: 1440, right: 1440 } },
        },
        children: [...headerParagraphs, ...contentParagraphs],
      }],
    });

    const blob = await Packer.toBlob(doc);

    if (!blob || !(blob instanceof Blob) || blob.size === 0) {
      throw new Error('Generated Word document is empty or invalid');
    }

    const safeName = String(filename || 'document').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 100) || 'document';
    triggerBlobDownload(blob, `${safeName}.docx`);
    toast.success('Document downloaded successfully!');
  } catch (err) {
    console.error('Word export error:', err);
    toast.error(`Failed to generate Word document: ${err?.message || 'Unknown error'}`);
  }
};
